"use client";

import {
	Col as DSCol,
	Grid,
	Text,
	type ColProps
} from "@pamperedchef/design-system";
import { type FC, type ReactNode } from "react";

import { ProductCard, type ProductCardProduct } from "../ProductCard";
import * as styles from "./ProductGrid.module.scss";

// `ColProps` picks up `React.HTMLAttributes` from a `framer-motion` import in
// the DS types, so `className` is missing from it — add it back on the cast.
const Col = DSCol as FC<
	ColProps & { children?: ReactNode; className?: string }
>;

const CARD_SPAN = { size: 6, sm: 6, md: 4 } as const;
const DEFAULT_EMPTY_MESSAGE = "No products match this category yet.";

export interface ProductGridProps {
	/** Already projected and already ordered by the caller. */
	products: ProductCardProduct[];
	emptyMessage?: string;
}

/**
 * A grid of product cards. Deliberately has no cart wiring — that needs app
 * stores and hooks a composite may not import, so the card renders without an
 * action here and a feature supplies one when it has somewhere to put it.
 */
export function ProductGrid({
	products,
	emptyMessage = DEFAULT_EMPTY_MESSAGE
}: ProductGridProps): JSX.Element {
	if (products.length === 0) {
		return (
			<Text tag="p" className={styles["grid__empty"]}>
				{emptyMessage}
			</Text>
		);
	}

	return (
		<Grid
			columns={12}
			gap="lg"
			className={styles["grid"]}
			data-component="ProductGrid"
		>
			{products.map((product) => (
				<Col
					key={product.sku}
					{...CARD_SPAN}
					className={styles["grid__cell"]}
				>
					<ProductCard product={product} />
				</Col>
			))}
		</Grid>
	);
}
